import "../ui/styles/daily.css";
import { DailyOutcome, DailyRoadMode, dailyRoadFromSave, dailyRoadSection } from "@highmarch/app";
import { utcDay } from "@highmarch/domain";
import { toast } from "../ui/toast";
import { showDailySheet } from "../ui/modes/dailySheet";
import { dailyEndHtml } from "../ui/modes/dailyEnd";
import { fmtCompact } from "../ui/kit";
import { fmtLeft } from "../ui/hub/eventsSheet";
import { eventClock } from "./events";
import { EndScreenFacts, Feature, HubApi, MORE_ORDER } from "./registry";

/** The Daily Road row sits between Events and Endless on the Events tab. */
const DAILY_ORDER = MORE_ORDER.events + 1;
/** Its block sits where the Iron/Endless block does on the end screen (before the events' block, END_ORDER 20 there). */
const END_ORDER = 15;
/** Run outcomes kept for end-screen redraws. */
const OUTCOMES_KEPT = 4;
/** The row shows from this many levels before it opens (as Endless's does). */
const TEASE_LEVELS = 5;

/**
 * Daily Road (MP-20, docs/MODES.md §4): the Events tab's row (red dot until today's road is played), its sheet (today's
 * road, best, reward, PLAY, the last days), the end screen's score and reward block, a Guide topic, the `dailyRoad` save
 * section bound to the save, and debug hooks for captures. Rules live in @highmarch/app dailyRoad.ts; the run itself is built
 * by the level flow from the fair profile (app runProfile.ts).
 */
export const dailyRoadFeature: Feature = {
  id: "dailyRoad",
  register(ctx, registry) {
    const daily = new DailyRoadMode(ctx.catalog, dailyRoadFromSave(ctx.save));
    dailyRoadSection.bind(ctx.save, () => daily.state.toJson());
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    const start = () => { const road = daily.today(now()); registry.startRun(road.level, road.modifiers); };
    const open = (parent: HTMLElement) => showDailySheet(parent, { catalog: ctx.catalog, daily, now, start });
    const playedToday = () => daily.best(now()) !== null;

    registry.moreRow({
      id: "daily", order: DAILY_ORDER, icon: "dailyRoad", home: "events",
      label: () => "Daily Road",
      note: () => {
        if (!daily.open) return `Clear level ${daily.unlockAfterLevel} to unlock`;
        const t = now(), best = daily.best(t);
        if (daily.paidToday(t)) return `Won · next in ${fmtLeft(daily.endsAt(t) - t)}`;
        return best ? `Best ${fmtCompact(best.score)} · a win pays` : "A new road today";
      },
      dot: () => (daily.open && !playedToday() ? 1 : 0),
      visible: () => daily.open || ctx.catalog.highestLevelCleared >= daily.unlockAfterLevel - TEASE_LEVELS,
      open: (hub: HubApi) => { if (daily.open) open(hub.parent); else toast(hub.parent, `Daily Road opens after level ${daily.unlockAfterLevel}`); },
    });

    // The end screen: a run's outcome is settled once (score, the day's reward, the event) and drawn from the cache.
    const outcomes = new Map<string, DailyOutcome | null>();
    const outcomeFor = (f: EndScreenFacts): DailyOutcome | null => {
      const key = `daily:${f.sim.seed}:${f.won ? "w" : "l"}${f.sim.stagesCleared}:${f.sim.tick}`;
      if (!outcomes.has(key)) {
        const o = daily.finishRun({ modifiers: f.sim.modifiers, level: f.level, won: f.won, stagesCleared: f.sim.stagesCleared, baseHp: f.sim.baseHp, road: f.sim.road.roadCells.length, key, now: now() });
        outcomes.set(key, o);
        if (outcomes.size > OUTCOMES_KEPT) outcomes.delete(outcomes.keys().next().value!);
        ctx.persist();
      }
      return outcomes.get(key) ?? null;
    };
    registry.endScreenExtra({ id: "dailyRoad", order: END_ORDER, html: (f) => { if (!f.sim.isDaily) return ""; const o = outcomeFor(f); return o ? dailyEndHtml(o) : ""; } });

    registry.guideTopic({
      id: "daily", group: "castle", label: "Daily Road", art: { icon: "map" }, fromLevel: daily.unlockAfterLevel + 1, steps: [],
      lines: [
        "**Daily Road** is one road a day, the same for every Commander: same map, same cards, same waves.",
        ctx.catalog.balance.modes.normalised.allowVip ? "Every tower stands at one level and gear stays home; your VIP bonus counts. The day's first win pays essence and **Marks**."
          : "Every tower stands at one level, with no gear or VIP. Your first win each day pays essence and **Marks**.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures: open the sheet, start today's road, see the day's numbers.
      (window as unknown as { __tdDaily: unknown }).__tdDaily = { daily, open: () => open(ctx.ui), start, today: () => daily.today(now()), day: () => utcDay(now()) };
    }
  },
};
